import React, { useEffect, useMemo, useRef } from 'react';
import DataGrid, { Column, Editing, Lookup } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { FormRef, GroupItem, Label, SimpleItem, Tab, TabbedItem, TabPanelOptions } from "devextreme-react/form";
import ITirage from "../../models/ITirage";
import Guid from "../../models/Guid";
import { FormListBase, FormListBaseGrid, FormListBaseTab, FormListBaseToolbar } from '../base';
import useCustomDataSource, { IFormBaseDataSourceOptions, Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import useLookUpBase from '../../components/selectbox/LookupBase';
import GestionCatalogueProduits from '../gestion-catalogue-produits/gestion-catalogue-produits';
import IGestionTirage from '../../models/IGestionTirage';
import GestionPointsEau from '../gestion-points-eau/gestion-points-eau';
import GestionTiragesResultats from './gestion-tirages-resultats';

export default function GestionTirages() {
  const typeUniteDataSourceOption: IFormBaseDataSourceOptions<IGestionTirage> = useMemo(() => ({
    path: "tirage",
    customReadByIdPath: "tirage/{key}/gestion",
    customSauvegarderPath: "tirage/save",
    primaryKey: "idTirage"
  }), []);
  const tirageFormDataSource = useCustomDataSource<ITirage, IGestionTirage>(typeUniteDataSourceOption);
  const { setMainForm, selectedDataRow: selectedTirage, onFieldDataChanged, mode, setMode, setSelectedDataRow, isFieldModifier, setIsFieldModifier } = tirageFormDataSource;
  const { editorOptions: produitPourTirageEditorOption, PopupGestion: PopupGestionCatalogue } =
    useLookUpBase({
      isModeModifier: mode !== Mode.Lecture,
      controllerName: "catalogue",
      showClear: mode !== Mode.Lecture,
      onClearRow: () => {
        setIsFieldModifier(true);
        formRef.current?.instance().updateData("idProduitOption", Guid.empty.toString());
      },
      titre: "Catalogue de produits"
    });
  const { editorOptions: pointEauEditorOption, PopupGestion: PopupGestionPointEau } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: 'pointeau',
    showClear: mode !== Mode.Lecture,
    width: '220px',
    titre: "Gestionnaire des points d'eau",
    onClearRow: () => {
      setIsFieldModifier(true);
      setSelectedDataRow((prev) => ({
        ...prev,
        idPointEau: Guid.empty,
      }));
    },
  });
  const formRef = useRef<FormRef>(null);

  const descriptionOptions = useMemo(() => (
    { maxLength: 100 }
  ), []);
  const officialiseOptions = useMemo(() => (
    { text: "Officialisé" }
  ), []);
  const actifOptions = useMemo(() => (
    { text: "Actif" }
  ), []);
  const spinsOptions = useMemo(() => (
    { min: 1, showSpinButtons: true, width: "45%" }
  ), []);
  const heightOptions = useMemo(() => (
    { height: 350 }
  ), []);
  const disponibleSurPavOptions = useMemo(() => (
    { text: "Disponible sur le poste d'accueil virtuel (PAV)" }
  ), []);
  const tirageGratuitOptions = useMemo(() => (
    { text: "Tirage gratuit" }
  ), []);
  const dateEditorOptions = useMemo(() => (
    {
      width: "55%",
      type: "datetime",
      displayFormat: "dd-MM-yyyy HH:mm",
    }
  ), []);
  const date2EditorOptions = useMemo(() => (
    {
      width: "20%",
      type: "date",
      displayFormat: "dd-MM-yyyy",
    }
  ), []);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  });

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
  }, [mode]);

  console.log(selectedTirage);
  return (
    <FormListBase data={tirageFormDataSource}>
      <FormListBaseGrid dataSource={tirageFormDataSource.dataSource}
        onFocusedRowChanged={() => {
          if (mode === Mode.Modifier) {
            setMode(Mode.Lecture)
          }
        }}
      >
        <Column key={"grid-col-date-tirage"} dataField={"dateTirage"} caption="Date tirage" allowHiding={false} />
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        <Column key={"grid-col-id-produit-tirage"} dataField={"idProduitTirage"} caption="Produit tirage" allowHiding={false} >
          <Lookup {...produitPourTirageEditorOption} />
        </Column>
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedTirage?.description || ''} `} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      <FormListBaseTab>
        <PopupGestionCatalogue onSelectionchanged={(data: any) => {
          setIsFieldModifier(true);
          formRef.current?.instance().updateData("idProduitTirage", data.idProduit);
        }}
        >
          <GestionCatalogueProduits isModeSelection={true} />
        </PopupGestionCatalogue>
        <PopupGestionPointEau
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            setSelectedDataRow((prev) => ({
              ...prev,
              idPointEau: data.idPointEau ?? data.id,
            }));
          }}
        >
          <GestionPointsEau isModeSelection={true} />
        </PopupGestionPointEau>
        <TabItem title="Informations">
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <div style={{ width: "40%", minWidth: "300px" }}>
              <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedTirage || {} as ITirage} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
                <GroupItem colCount={3}>
                  <SimpleItem
                    key="frm-description"
                    dataField="description"
                    editorOptions={descriptionOptions}
                    colSpan={2}
                  />
                  <SimpleItem
                    key="frm-officialise"
                    dataField="officialise"
                    editorType="dxCheckBox"
                    editorOptions={officialiseOptions}
                    colSpan={1}
                  >
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
                <SimpleItem key={"frm-date-tirage"} dataField={"dateTirage"} editorType={"dxDateBox"}
                  editorOptions={dateEditorOptions}
                >
                  <Label text={"Date du tirage"} />
                </SimpleItem>
                <SimpleItem key={"frm-date-vente-debut"} dataField={"dateVenteDebut"} editorType={"dxDateBox"}
                  editorOptions={dateEditorOptions}
                >
                  <Label text={"Date de vente début"} />
                </SimpleItem>
                <SimpleItem key={"frm-date-vente-fin"} dataField={"dateVenteFin"} editorType={"dxDateBox"}
                  editorOptions={dateEditorOptions}
                >
                  <Label text={"Date de vente fin"} />
                </SimpleItem>
                <SimpleItem key={"frm-id-produit-tirage"} dataField={"idProduitTirage"}
                  editorType={"dxSelectBox"}
                  editorOptions={produitPourTirageEditorOption}
                >
                  <Label text={"Produit pour tirage"} />
                </SimpleItem>
                <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"}
                  editorOptions={actifOptions}
                >
                  <Label visible={false} />
                </SimpleItem>
              </Form>
            </div>
            <div
            // style={{ width: "50%", minWidth: "350px" }}
            >
              <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedTirage || {} as ITirage} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
                <GroupItem caption={"Options"}>
                  <SimpleItem key={"frm-nombre-gagnants-max"} dataField={"nombreGagnantsMax"} editorType={"dxNumberBox"} editorOptions={spinsOptions}
                  >
                    <Label text={"Nombre limite de fois qu'un client peut gagner"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-nombre-sortie-gagnant-client"} dataField={"nombreSortieGagnantClient"} editorType={"dxNumberBox"} editorOptions={spinsOptions}
                  >
                    <Label text={"Nombre limite de résultats pour le tirage"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-limite-chance"} dataField={"limiteChance"} editorType={"dxNumberBox"} editorOptions={spinsOptions}
                  >
                    <Label text={"Nombre limite de chances pour un client (0 = infini)"} />
                  </SimpleItem>
                  <SimpleItem>
                    <div style={{ marginTop: "1.3em" }}>
                      <label style={{ fontWeight: "bold" }}>Délai de tirage</label>
                    </div>
                  </SimpleItem>
                  <SimpleItem key={"frm-nombre-gagnants-passe1"} dataField={"nombreGagnantsPasse1"} editorType={"dxNumberBox"} editorOptions={spinsOptions}
                  >
                    <Label text={"Nombre de gagnants pour la première passe du tirage"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-delai-passe1"} dataField={"delaiPasse1"} editorType={"dxNumberBox"} editorOptions={spinsOptions}
                  >
                    <Label text={"Délai du tirage (en secondes) pour la première passe"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-delai-passe2"} dataField={"delaiPasse2"} editorType={"dxNumberBox"} editorOptions={spinsOptions}
                  >
                    <Label text={"Délai du tirage (en secondes) pour la deuxième passe"} />
                  </SimpleItem>
                </GroupItem>
              </Form>
            </div>
          </div>
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedTirage || {} as ITirage} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
            <SimpleItem>
              <label style={{ fontWeight: "bold" }}>Message à afficher sur les documents</label>
            </SimpleItem>
            <TabbedItem>
              <TabPanelOptions height={"100%"} />
              <Tab title={"Français"}>
                <SimpleItem key={"frm-message-legal"} dataField={"messageLegal"} editorType={"dxTextArea"} editorOptions={heightOptions}>
                  <Label visible={false} />
                </SimpleItem>
              </Tab>
              <Tab title={"Anglais"}>
                <SimpleItem key={"frm-message-legal-en"} dataField={"messageLegalEn"} editorType={"dxTextArea"} editorOptions={heightOptions}>
                  <Label visible={false} />
                </SimpleItem>
              </Tab>
            </TabbedItem>
          </Form>
        </TabItem>
        <TabItem title="Inscriptions">
          <Form readOnly={true} alignItemLabelsInAllGroups={true} formData={selectedTirage || {} as ITirage} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <GroupItem>
              <DataGrid
                showBorders={true}
                allowColumnResizing={true}
                dataSource={selectedTirage?.inscriptions ?? []}
                keyExpr="idTirageParticipant"
                allowColumnReordering={false}
                noDataText={"Aucune inscription"}
              >
                <Editing mode="cell" allowAdding={false} allowUpdating={false} allowDeleting={false} />
                <Column dataField={"client"} dataType='string' caption={"Client"} />
                <Column dataField={"adresse"} dataType='string' caption={"Adresse"} />
                <Column dataField={"ville"} dataType='string' caption={"Ville"} />
                <Column dataField={"telephone"} dataType='string' caption={"Téléphone"} />
                <Column dataField={"substitut"} dataType='string' caption={"Substitut"} />
                <Column dataField={"chances"} dataType='number' caption={"Chances"} />
              </DataGrid>
            </GroupItem>
          </Form>
        </TabItem>
        <GestionTiragesResultats
          mode={mode}
          selectedTirage={selectedTirage}
          onFieldDataChanged={onFieldDataChanged}
        />
        <TabItem title="Internet">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedTirage || {} as ITirage} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
            <GroupItem>
              <SimpleItem key={"frm-disponible-sur-pav"} dataField={"disponibleSurPav"} editorType={"dxCheckBox"} editorOptions={disponibleSurPavOptions}>
                <Label visible={false} />
              </SimpleItem>
              <SimpleItem key={"frm-date-activite"} dataField={"dateActivite"} editorType={"dxDateBox"} editorOptions={date2EditorOptions}>
                <Label text={"Date de l'activité"} />
              </SimpleItem>
              <SimpleItem key={"frm-point-eau"} dataField={"idPointEau"} editorType={"dxSelectBox"} editorOptions={pointEauEditorOption}>
                <Label text={"Point d'eau"} />
              </SimpleItem>
              <SimpleItem key={"frm-tirage-gratuit"} dataField={"tirageGratuit"} editorType={"dxCheckBox"} editorOptions={tirageGratuitOptions}>
                <Label visible={false} />
              </SimpleItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedTirage || {} as ITirage} /></TabItem>
      </FormListBaseTab>
    </FormListBase>
  );
}